import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import UserAvatar, { userDisplayName } from '@/components/brand/UserAvatar';
import { getSubscriptionState } from '@/utils/subscription';
import { useMemberApp } from '../AppProvider';
import { IconArrow } from '../components/Icons';
import { memberPath } from '@/config/hosts';

const PLANS = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: '$19.99',
    period: 'per month',
    note: 'Cancel anytime',
  },
  {
    id: 'yearly',
    name: 'Yearly',
    price: '$14.99',
    period: 'per month',
    note: '$179.88 billed once a year · save 25%',
    featured: true,
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    price: '$999',
    period: 'once',
    note: 'One payment. No renewals.',
  },
];

function creditSummary(credits, active) {
  if (!credits || typeof credits.balance !== 'number') return null;
  const textCost = Math.max(1, credits.operations?.TEXT_QUERY || 2);
  const voiceCost = Math.max(1, credits.operations?.VOICE_TURN || 6);
  const texts = Math.floor(credits.balance / textCost);
  const voices = Math.floor(credits.balance / voiceCost);
  return {
    line: active
      ? `Your plan is active. ${credits.balance} credits left, about ${texts} text questions or ${voices} voice turns.`
      : `${credits.balance} credits left. That is about ${texts} text questions, or ${voices} voice turns.`,
    hint: 'A text question uses 2 credits. A voice turn uses 6.',
  };
}

export default function AccountPage() {
  const { user, logout } = useAuth();
  const { isPro, setUpgradeOpen, setPricingFocus, refresh, credits, freeRemaining } = useMemberApp();
  const [state, setState] = useState(null);
  const [checking, setChecking] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelMsg, setCancelMsg] = useState('');
  const active = isPro || state?.isActive;
  const name = userDisplayName(user) === 'Account' ? 'There' : userDisplayName(user);
  const email = user?.email || '';
  const allowance = creditSummary(credits, active);

  useEffect(() => {
    getSubscriptionState(user).then(setState);
  }, [user, isPro]);

  const openPlan = (id) => {
    setPricingFocus(id);
    setUpgradeOpen(true);
  };

  const cancelPlan = async (refund) => {
    setCancelling(true);
    setCancelMsg('');
    try {
      const { apiUrl } = await import('@/config/api');
      const response = await fetch(apiUrl('/api/payments/subscription/cancel'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refund: Boolean(refund) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Cancellation failed');
      }
      setCancelMsg(refund && data.refunded ? 'Cancelled, and a refund was requested.' : 'Your subscription is cancelled.');
      await refresh();
      const next = await getSubscriptionState(user);
      setState(next);
    } catch (err) {
      setCancelMsg(err.message || 'Cancellation failed');
    } finally {
      setCancelling(false);
    }
  };

  const checkAgain = async () => {
    setChecking(true);
    await refresh();
    const next = await getSubscriptionState(user);
    setState(next);
    setChecking(false);
  };

  const nextBill = state?.expiresAt
    ? new Date(state.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  const planTitle = state?.isAdmin
    ? 'Team access'
    : state?.lifetime
      ? 'Lifetime'
      : active
        ? 'ByOnco PRO'
        : 'Start with Vani';

  const planBody = state?.isAdmin
    ? 'Included with your ByOnco account. No card on file.'
    : state?.lifetime
      ? 'One payment. There is no renewal date.'
      : active && nextBill
        ? `${state?.planLabel || '$19.99/month'}. Next date ${nextBill}${state?.daysLeft != null ? `. ${state.daysLeft} days left.` : ''}`
        : 'Keep Vani beside you between visits. $19.99 a month, $14.99 a month billed yearly, or $999 once.';

  return (
    <div className="by-page">
      <div className="by-center">
        <header className="by-page-head">
          <h1>Account</h1>
          <p className="by-lede">Your plan, what is left in this period, and a quiet way to change it.</p>
        </header>

        <Link className="by-id-row" to={memberPath('/settings')}>
          <UserAvatar user={user} className="by-avatar" />
          <span className="by-id-meta">
            <b>{name}</b>
            <small>{email || 'Add your name in Settings'}</small>
          </span>
          <span className="by-chev" aria-hidden="true"><IconArrow /></span>
        </Link>

        <div className="by-bill">
          <div className="by-bill-top">
            <div>
              <p className="by-bill-kicker">{active ? 'Current plan' : 'No plan yet'}</p>
              <h2>{planTitle}</h2>
            </div>
            <span className={`by-bill-pill${active ? ' is-on' : ''}`}>{active ? 'Active' : 'Inactive'}</span>
          </div>
          <p className="by-lede" style={{ margin: '10px 0 0', maxWidth: 'none' }}>{planBody}</p>
          {allowance ? (
            <div className="by-credit">
              <div>
                <p>{allowance.line}</p>
                <small>{allowance.hint}</small>
              </div>
            </div>
          ) : null}
          <div className="by-row" style={{ marginTop: 18 }}>
            {active ? (
              <>
                {!state?.lifetime && !state?.isAdmin ? (
                  <button type="button" className="by-btn by-btn-dark" onClick={() => openPlan('lifetime')}>
                    Switch to lifetime
                  </button>
                ) : null}
                <button type="button" className="by-btn by-btn-ghost" disabled={checking} onClick={checkAgain}>
                  {checking ? 'Checking…' : 'Refresh status'}
                </button>
                {!state?.isAdmin ? (
                  <>
                    <button type="button" className="by-btn by-btn-ghost" disabled={cancelling} onClick={() => cancelPlan(false)}>
                      {cancelling ? 'Cancelling…' : 'Cancel plan'}
                    </button>
                    <button type="button" className="by-btn by-btn-danger" disabled={cancelling} onClick={() => cancelPlan(true)}>
                      Cancel and refund
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <button type="button" className="by-btn by-btn-dark" onClick={() => openPlan('yearly')}>
                Choose a plan
              </button>
            )}
          </div>
          {cancelMsg ? <p className={`by-save-note${/fail/i.test(cancelMsg) ? ' is-err' : ' is-ok'}`}>{cancelMsg}</p> : null}
        </div>

        {!active ? (
          <div className="by-bill-plans">
            {PLANS.map((plan) => (
              <button
                key={plan.id}
                type="button"
                className={`by-bill-plan${plan.featured ? ' is-featured' : ''}`}
                onClick={() => openPlan(plan.id)}
              >
                {plan.featured ? <span className="by-bill-plan-tag">Most chosen</span> : null}
                <b>{plan.name}</b>
                <span className="by-bill-plan-price">
                  <strong>{plan.price}</strong>
                  <em>{plan.period}</em>
                </span>
                <small>{plan.note}</small>
              </button>
            ))}
          </div>
        ) : null}

        <div className="by-includes">
          <h2>What Vani is for</h2>
          <ul>
            <li>Side effects, food this cycle, medicines, and labs in plain language, so you are not waiting a week for a simple answer.</li>
            <li>Questions to take into clinic, written the way you would actually say them.</li>
            <li>Reports you attach are read here, not guessed from the open internet.</li>
            <li>Voice covers the same ground as typing. Confirm anything medical with your oncology team.</li>
          </ul>
        </div>

        <div className="by-setting">
          <h2>Billing</h2>
          <p className="by-lede" style={{ margin: 0, maxWidth: 'none' }}>
            Cards go through Razorpay. Access turns on as soon as payment confirms.
            For a receipt or a refund, email{' '}
            <a href="mailto:care@getbyonco.com">care@getbyonco.com</a>.
          </p>
        </div>

        <div className="by-session">
          <p>Signed in as {email || name}</p>
          <button type="button" className="by-btn by-btn-ghost" onClick={() => logout()}>Log out</button>
        </div>
      </div>
    </div>
  );
}
